<template>
  <div v-bind="$attrs" class="tw-flex tw-flex-col tw-items-stretch tw-text-bridgit-navy lg:tw-flex-row">
    <div>
      <h2 class="tw-mb-4 tw-text-lg tw-font-bold md:tw-text-xl lg:tw-text-2xl">
        {{ loanType === 'bridging' ? 'Buy now, sell later' : 'Unlockit, sell later' }}
      </h2>
      <div class="tw-grid tw-grid-cols-1 tw-items-end tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-1">
        <AmountInput
          id="newLoanAmount"
          v-model="data.newLoanAmount"
          :label="loanType === 'bridging' ? 'New property purchase price' : 'How much do you want to borrow?'"
          :slider-min="loanType === 'bridging' ? 100000 : 75000"
          :slider-max="loanType === 'bridging' ? 7000000 : 3000000"
        />
        <AmountInput
          v-if="loanType === 'bridging'"
          id="deposit"
          v-model="data.deposit"
          label="Contribution to purchase (incl. deposit)"
          :slider-min="0"
          :slider-max="2000000"
        />
        <AmountInput
          id="existingPropertyValue"
          v-model="data.existingPropertyValue"
          label="Value of property being sold"
          :slider-min="100000"
          :slider-max="7000000"
        />
        <AmountInput
          id="existingMortgage"
          v-model="data.existingMortgage"
          label="Mortgage on property being sold"
          :slider-min="0"
          :slider-max="7000000"
        />
      </div>
    </div>
    <div
      class="tw-mt-5 tw-flex tw-flex-1 tw-flex-col tw-items-center md:tw-rounded-xl md:tw-border-2 md:tw-border-gray-100 md:tw-p-5 lg:tw-mt-0 lg:tw-ml-5"
    >
      <div class="tw-mb-5 tw-hidden tw-h-[255px] tw-items-end tw-gap-5 tw-text-bridgit-navy xl:tw-flex">
        <HousePicto v-if="loanType === 'bridging'" :style="pictoStyle(0)">
          <div class="tw-mb-4 tw-text-center">
            <div class="tw-leading-tight">Buy new property<br />(less contribution)</div>
            <div class="tw-pt-2 tw-text-xl tw-font-bold">
              {{ formatValue(netLoanAmount) }}
            </div>
          </div>
        </HousePicto>
        <PadlockPicto v-else :style="pictoStyle(0)">
          <div class="tw-mb-4 tw-text-center">
            <div class="tw-leading-tight">Loan amount</div>
            <div class="tw-pt-2 tw-text-xl tw-font-bold">
              {{ formatValue(netLoanAmount) }}
            </div>
          </div>
        </PadlockPicto>
        <div
          class="tw-mb-10 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-bridgit-mayaBlue-50 tw-pt-0.5 tw-text-xl tw-font-bold tw-leading-none tw-text-bridgit-navy"
        >
          +
        </div>
        <HousePicto :style="pictoStyle(1)">
          <div class="tw-mb-4 tw-text-center">
            <div class="tw-leading-tight">Existing<br />mortgage</div>
            <div class="tw-pt-2 tw-text-xl tw-font-bold">
              {{ formatValue(data.existingMortgage) }}
            </div>
          </div>
        </HousePicto>
        <div
          class="tw-mb-10 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-bridgit-mayaBlue-50 tw-pt-0.5 tw-text-xl tw-font-bold tw-leading-none tw-text-bridgit-navy"
        >
          -
        </div>
        <HousePicto :style="pictoStyle(2)">
          <div class="tw-mb-4 tw-text-center">
            <div class="tw-leading-tight">Sell existing<br />property</div>
            <div class="tw-pt-2 tw-text-xl tw-font-bold">
              {{ formatValue(data.existingPropertyValue) }}
            </div>
          </div>
        </HousePicto>
      </div>
      <Button class="tw-w-full sm:tw-hidden" variant="primary" @click="detailsOpen = true"> Calculate </Button>
      <div
        class="tw-hidden tw-w-full tw-rounded-lg tw-p-5 tw-text-center tw-text-bridgit-navy sm:tw-block"
        :class="loanType === 'bridging' ? 'tw-bg-bridgit-mayaBlue-25' : 'tw-bg-bridgit-lightBlue'"
      >
        <div class="tw-mb-5 tw-text-xl tw-font-bold">Your estimated loan calculation</div>
        <div v-show="!lvr || lvr <= 75" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2">
          <div class="tw-pb-3 sm:tw-border-r-2 sm:tw-border-r-bridgit-navy sm:tw-pr-5 sm:tw-pb-0 sm:tw-text-right">
            <div>Estimated principal amount</div>
            <div class="tw-mt-2 tw-text-3xl tw-font-bold">
              {{ formatValue(totalLoanAmount) }}
            </div>
          </div>
          <div class="sm:tw-pl-5 sm:tw-text-left">
            <div v-show="endDebt <= 0" class="tw-flex tw-justify-center sm:tw-justify-start">
              Your cash after sale
              <div
                v-tippy="
                  'This figure is for illustrative purposes and is an estimate only, it does not take into account interest, fees and charges.'
                "
                class="tw-ml-2 tw-flex tw-items-start"
              >
                <InformationCircleIcon class="tw-h-6 tw-w-6 tw-cursor-pointer" aria-hidden="true" />
              </div>
            </div>
            <div v-show="endDebt > 0" class="tw-flex tw-justify-center sm:tw-justify-start">
              End loan after sale
              <div
                v-tippy="
                  'Based on the value of your sale property, you will have an end loan remaining. Meaning, before the bridging period ends you simply need to refinance to a standard home loan.'
                "
                class="tw-ml-2 tw-flex tw-items-start"
              >
                <InformationCircleIcon class="tw-h-6 tw-w-6 tw-cursor-pointer" aria-hidden="true" />
              </div>
            </div>
            <div class="tw-mt-2 tw-text-3xl tw-font-bold">
              {{ formatValue(endDebt, true) }}
            </div>
          </div>
        </div>
        <div v-show="lvr && lvr > 75">
          <div>Estimated principal amount</div>
          <div
            class="tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-text-3xl tw-font-medium sm:tw-flex-row sm:tw-justify-center"
          >
            <div class="tw-font-bold">{{ formatValue(totalLoanAmount) }}</div>
            <div class="tw-flex tw-justify-center">
              (LVR&nbsp;{{ lvr ? lvr.toFixed(0) : '' }}%)
              <div
                v-tippy="
                  'Loan to Value Ratio (LVR) is the amount of money you are borrowing shown as a percentage. This is based on the combined value of the property you are buying and the property you are selling.'
                "
                class="tw-ml-2 tw-flex tw-items-start"
              >
                <InformationCircleIcon class="tw-h-6 tw-w-6 tw-cursor-pointer" aria-hidden="true" />
              </div>
            </div>
          </div>
          <div class="tw-mt-2 lg:tw-max-w-[622px]">
            Based on the information provided your Loan to Value Ratio (LVR) may be too high. To help you we require
            some additional information; feel free to get a quote or schedule a call with our team.
          </div>
        </div>
        <div class="tw-mt-5 tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-center">
          <Button ref="btnApplyNow" variant="primary" :link-href="getAQuoteUrl" link-target="_blank">
            <span class="tw-font-bold">Get a quote</span>
          </Button>
          <Button v-show="!lvr || lvr <= 75" variant="secondary" @click="detailsOpen = true">
            <span class="tw-font-bold">Loan details</span>
          </Button>
          <Button v-show="lvr && lvr > 75" variant="link" :link-href="scheduleCallUrl" link-target="_blank">
            <span class="tw-font-bold">Schedule a call</span>
          </Button>
        </div>
        <div class="tw-mt-4 tw-text-bridgit-navy">
          <p class="tw-mt-2 tw-text-sm">
            Please submit an application or quote to receive a detailed breakdown of your loan amount.
          </p>
        </div>
      </div>
    </div>
  </div>
  <LoanDetails
    :is-open="detailsOpen"
    :loan-type="loanType"
    :new-loan-amount="data.newLoanAmount"
    :deposit="data.deposit"
    :existing-property-value="data.existingPropertyValue"
    :existing-mortgage="data.existingMortgage"
    :total-loan-amount="totalLoanAmount"
    :lvr="lvr"
    :apply-now-url="btnApplyNow?.$el?.href || undefined"
    @close="detailsOpen = false"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { LoanType } from '@/helpers/common';
import { TippyDirective } from '@/helpers/tippy';
import AmountInput from './AmountInput.vue';
import Button from './Button.vue';
import HousePicto from './HousePicto.vue';
import PadlockPicto from './PadlockPicto.vue';
import LoanDetails from './LoanDetails.vue';

interface Data {
  newLoanAmount?: number;
  deposit?: number;
  existingPropertyValue?: number;
  existingMortgage?: number;
}

const formatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const getAQuoteUrl = import.meta.env.VITE_GET_A_QUOTE_URL;
const scheduleCallUrl = import.meta.env.VITE_SCHEDULE_CALL_URL;
const vTippy = TippyDirective;

const props = defineProps<{
  loanType: LoanType;
}>();

const data = reactive<Data>(
  props.loanType === 'bridging'
    ? {
        newLoanAmount: 1000000,
        deposit: 200000,
        existingPropertyValue: 2000000,
        existingMortgage: 500000,
      }
    : {
        newLoanAmount: 100000,
        deposit: 0,
        existingPropertyValue: 2000000,
        existingMortgage: 500000,
      },
);

// note: link href may be modified by containing page for tracking purposes
// need to pass current target URL when opening loan details
const btnApplyNow = ref<typeof Button | null>(null);
const detailsOpen = ref(false);

const netLoanAmount = computed(() => {
  if (data.newLoanAmount === undefined && data.deposit === undefined) {
    return undefined;
  }
  return Math.max((data.newLoanAmount ?? 0) - (data.deposit ?? 0), 0);
});
const totalLoanAmount = computed(() => (netLoanAmount.value ?? 0) + (data.existingMortgage ?? 0));
const endDebt = computed(() => totalLoanAmount.value - (data.existingPropertyValue ?? 0));
const lvr = computed(() => {
  const totalValue =
    props.loanType === 'bridging'
      ? (data.newLoanAmount ?? 0) + (data.existingPropertyValue ?? 0)
      : data.existingPropertyValue ?? 0;
  return totalValue ? (totalLoanAmount.value / totalValue) * 100 : undefined;
});

const pictoSizes = computed(() => {
  const absoluteValues = [netLoanAmount.value ?? 0, data.existingMortgage ?? 0, data.existingPropertyValue ?? 0];
  const min = Math.min(...absoluteValues);
  const max = Math.max(...absoluteValues);
  if (min === max) {
    return [1, 1, 1];
  }
  const relativeValues = absoluteValues.map((value) => (value === min ? 1 : min === 0 ? value : value / min));
  const multiplier = 0.5 / Math.max(...relativeValues);
  return relativeValues.map((value) => 1 + (value - 1) * multiplier);
});

function formatValue(value?: number, absoluteValue = false) {
  if (value === undefined) {
    return '$ -';
  }
  return absoluteValue ? formatter.format(Math.abs(value)) : formatter.format(value);
}

function pictoStyle(index: number) {
  const size = Math.trunc(pictoSizes.value[index] * 170);
  return `height: ${size}px; width: 170px;`;
}
</script>
